import { APP_READER, CUSTOM_APP_READER, Role } from "@xapp/stentor-api-client/dist/models/Roles";

import { AuthState, Credentials, UserProfile } from "./state";

export const KEY_USER_PROFILE = "key_user_profile";
export const KEY_CODE_VERIFIER = "key_code_verifier";
export const KEY_CREDENTIALS = "key_credentials";
export const KEY_REDIRECT = "key_redirect_url";
export const KEY_STATE = "key_state";
export const KEY_AWS_ID = "key_aws_id";

/**
 * Determines whether or not a user should be granted admin privileges
 *
 * @export
 * @param {UserProfile} profile
 * @returns {boolean}
 */
export function isAdmin(profile: Pick<UserProfile, "isAdmin">): boolean {
    return !!profile && !!profile.isAdmin;
}

/**
 * Determines whether a user has a specific role for an org
 *
 * @export
 * @param {string} organizationId
 * @param {OrganizationRole} role
 * @param {UserProfile} profile
 * @returns
 */
export function hasOrgRole(organizationId: string, role: Role, profile: UserProfile) {
    if (profile) {
        const { roles = [] } = profile;
        const orgRole = roles.find(r => r.organizationId === organizationId);
        if (orgRole) {
            const { roles = [] } = orgRole;
            return roles.indexOf(role) !== -1;
        }
    }
    return false;
}

/**
 * Shortcut for the readonly roles
 *
 * @export
 * @param {string} organizationId
 * @param {UserProfile} profile
 * @returns
 */
export function isReadOnlyRole(organizationId: string, profile: UserProfile) {
    return hasOrgRole(organizationId, APP_READER, profile) || hasOrgRole(organizationId, CUSTOM_APP_READER, profile);
}

/**
 * Sets to local storage
 *
 * @param codeVerifier
 */
export function setCodeVerifier(codeVerifier: string): void {
    localStorage.setItem(KEY_CODE_VERIFIER, codeVerifier);
}

/**
 * Sets stored credentials to local storage
 *
 * @param credentials
 */
export function setStoredCredentials(credentials: Credentials): void {
    localStorage.setItem(KEY_CREDENTIALS, JSON.stringify(credentials));
}

/**
 * Sets profile on local storage
 *
 * @param profile
 */
export function setProfile(profile: UserProfile): void {
    localStorage.setItem(KEY_USER_PROFILE, JSON.stringify(profile));
}

/**
 * Sets redirect URL on local storage
 * @param url
 */
export function setRedirectUrl(url: string): void {
    localStorage.setItem(KEY_REDIRECT, url);
}

/**
 * Sets state parameter on local storage
 * @param state
 */
export function setStateParameter(state: string): void {
    localStorage.setItem(KEY_STATE, state);
}

/**
 * Sets the AWS ID on local storage
 *
 * @param awsId
 */
export function setAWSID(awsId: string): void {
    localStorage.setItem(KEY_AWS_ID, awsId);
}

/**
 * Removes AWS ID from local storage
 */
export function removeAWSID(): void {
    localStorage.removeItem(KEY_AWS_ID);
}

export function removeStoredAuthState(): void {
    localStorage.removeItem(KEY_USER_PROFILE);
    localStorage.removeItem(KEY_CREDENTIALS);
    localStorage.removeItem(KEY_CODE_VERIFIER);

    localStorage.removeItem(KEY_STATE);
    localStorage.removeItem(KEY_AWS_ID);
}

export function removeRedirectUrl(): void {
    localStorage.removeItem(KEY_REDIRECT);
}

export function removeStateParameter(): void {
    localStorage.removeItem(KEY_STATE);
}

export function getAWSID(): string {
    return localStorage.getItem(KEY_AWS_ID);
}

export function getCodeVerifier(): string {
    return localStorage.getItem(KEY_CODE_VERIFIER);
}

export function getStoredCredentials(): Credentials {
    try {
        const credentials = JSON.parse(localStorage.getItem(KEY_CREDENTIALS)) as Credentials;
        return credentials;
    } catch (error) {
        console.error("Error retrieving stored credentials state: " + error.message);
        removeStoredAuthState();
    }
}

export function getStoredAuthState(): Partial<AuthState> {
    try {
        const profile = JSON.parse(localStorage.getItem(KEY_USER_PROFILE)) as UserProfile;
        const credentials = JSON.parse(localStorage.getItem(KEY_CREDENTIALS)) as Credentials;
        return { profile, credentials };
    } catch (error) {
        console.error("Error retrieving stored auth state: " + error.message);
        removeStoredAuthState();
        return {};
    }
}

export function getStoredRedirectUrl(): string {
    try {
        return localStorage.getItem(KEY_REDIRECT);
    } catch (error) {
        console.error("Error retrieving redirect.", error);
        removeRedirectUrl();
        return undefined;
    }
}

export function getStoredStateParameter(): string {
    try {
        return localStorage.getItem(KEY_STATE);
    } catch (error) {
        console.error("Error retrieving redirect.", error);
        removeStateParameter();
        return undefined;
    }
}

/**
 * Cleans out the Cognito state from local storage
 */
export function removeCognitoState(): void {

    const clientId = window.xaStudioConfig?.auth?.userWebPoolClientId;

    localStorage.removeItem(`CognitoIdentityServiceProvider.${clientId}.inflightOAuth`);
    localStorage.removeItem(`CognitoIdentityServiceProvider.${clientId}.oauthPKCE`);
    localStorage.removeItem(`CognitoIdentityServiceProvider.${clientId}.oauthState`);
    localStorage.removeItem(`CognitoIdentityServiceProvider.${clientId}.refreshToken`);
    localStorage.removeItem(`CognitoIdentityServiceProvider.${clientId}.userData`);
    localStorage.removeItem(`CognitoIdentityServiceProvider.${clientId}.accessToken`);
    localStorage.removeItem(`CognitoIdentityServiceProvider.${clientId}.idToken`);
    localStorage.removeItem(`CognitoIdentityServiceProvider.${clientId}.clockDrift`);
}

export interface Auth0Session {
    /**
     * This is the user's access token which is used to access APIs.
     *
     * @type {string}
     * @memberof Auth0Session
     */
    accessToken: string;
    /**
     * The user's refresh token which can be used to retrieve a new token.
     *
     * @type {string}
     * @memberof Auth0Session
     */
    refreshToken?: string;
    /**
     * This is the amount of time, in seconds, in which the token will expire.
     *
     * @type {number}
     * @memberof Auth0Session
     */
    expiresIn: number;
}

/**
 * This will convert an Auth0 Session object in to a
 * Stentor Credentials object.
 *
 * This assumes that the object is brand-new and will set the expiration
 * accordingly.
 *
 * @export
 */
export function auth0SessionToCredentials(session: Auth0Session): Credentials {
    const expiresInMilliseconds = session.expiresIn * 1000;
    return {
        Expiration: new Date(Date.now() + expiresInMilliseconds).toISOString(),
        accessToken: session.accessToken,
        refreshToken: session.refreshToken
    };
}
